import React, { useMemo } from "react";
import { Line, Pie } from "react-chartjs-2";
import {
  UserPlus,
  Users,
  Utensils,
  ClipboardList,
  Filter,
  TrendingUp,
  BarChart3,
  PieChart as PieChartIcon,
  DollarSign,
} from "lucide-react";

interface User {
  id: string;
  first_name: string;
  last_name: string;
  email: string;
  created_at: string;
  last_login?: string;
  total_spent?: number;
  total_refunds?: number;
  avatar?: string;
  subscription: {
    plan: "free" | "premium" | "pro";
    status: "active" | "canceled" | "expired" | "past_due" | "unpaid";
    billing: "monthly" | "yearly";
    expiresAt: string;
    renewsAt?: string;
    cancelAt?: string;
    features: string[];
    stripeSubscriptionId?: string;
    stripeCustomerId?: string;
    priceId?: string;
    currentPeriodStart?: string;
    currentPeriodEnd?: string;
    discount?: {
      code: string;
      percentage: number;
      amount?: number;
      expiresAt?: string;
      description?: string;
    };
  } | null;
}

interface MonthlyData {
  [month: string]: {
    recipes: number;
    meal_plans: number;
  };
}

interface DashboardStats {
  new_users_this_month: number;
  users_by_type: {
    free: number;
    pro: number;
    premium: number;
  };
  all_users: User[];
  recipes_current_month: number;
  meal_plans_current_month: number;
  total_recipes: number;
  total_meal_plans: number;
  monthly_data: MonthlyData;
  current_month: number;
  current_year: number;
}

interface PeriodFilter {
  startDate: string | null;
  endDate: string | null;
}

interface PeriodStats {
  period: {
    startDate: string | null;
    endDate: string | null;
  };
  users: {
    free: number;
    pro: number;
    premium: number;
    total: number;
  };
  content: {
    recipes: number;
    meal_plans: number;
  };
}

interface DashboardContentProps {
  stats: DashboardStats;
  periodFilter: PeriodFilter;
  periodStats: PeriodStats | null;
  loadingPeriod: boolean;
  onStartDateChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onEndDateChange: (e: React.ChangeEvent<HTMLInputElement>) => void;
  onClearFilter: () => void;
}

const DashboardContent: React.FC<DashboardContentProps> = ({
  stats,
  periodFilter,
  periodStats,
  loadingPeriod,
  onStartDateChange,
  onEndDateChange,
  onClearFilter,
}) => {
  // Helper function to get month names
  const getMonthName = (month: number): string => {
    const monthNames = [
      "January", "February", "March", "April", "May", "June",
      "July", "August", "September", "October", "November", "December",
    ];
    return monthNames[month - 1];
  };

  // Helper function to format currency
  const formatCurrency = (value: number): string => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 2,
      maximumFractionDigits: 2,
    }).format(value);
  };

  // Calculate overall financial totals
  const financialTotals = useMemo(() => {
    let totalRevenue = 0;
    let totalRefunds = 0;

    stats.all_users.forEach(user => {
      totalRevenue += user.total_spent || 0;
      totalRefunds += user.total_refunds || 0;
    });

    return {
      totalRevenue,
      totalRefunds,
      netRevenue: totalRevenue - totalRefunds,
    };
  }, [stats.all_users]);

  // User Growth/Loss Chart - tracks number of users by plan over time
  const userGrowthData = useMemo(() => {
    const months = Array.from({ length: 12 }, (_, i) => getMonthName(i + 1));
    
    // Calculate monthly user counts by plan
    const monthlyUsersByPlan = {
      free: Array(12).fill(0),
      pro: Array(12).fill(0),
      premium: Array(12).fill(0)
    };

    stats.all_users.forEach(user => {
      const plan = user.subscription?.plan || 'free';
      const userRegistrationMonth = new Date(user.created_at).getMonth();
      
      // Add user to each month from registration onwards
      for (let i = userRegistrationMonth; i < 12; i++) {
        monthlyUsersByPlan[plan as keyof typeof monthlyUsersByPlan][i] += 1;
      }
    });

    return {
      labels: months,
      datasets: [
        {
          label: "Free Users",
          data: monthlyUsersByPlan.free,
          borderColor: "rgba(156, 163, 175, 1)",
          backgroundColor: "rgba(156, 163, 175, 0.1)",
          tension: 0.2,
          fill: true,
        },
        {
          label: "Pro Users",
          data: monthlyUsersByPlan.pro,
          borderColor: "rgba(249, 115, 22, 1)",
          backgroundColor: "rgba(249, 115, 22, 0.1)",
          tension: 0.2,
          fill: true,
        },
        {
          label: "Premium Users",
          data: monthlyUsersByPlan.premium,
          borderColor: "rgba(236, 72, 153, 1)",
          backgroundColor: "rgba(236, 72, 153, 0.1)",
          tension: 0.2,
          fill: true,
        },
      ],
    };
  }, [stats.all_users]);

  // Financial Totals Chart - shows total revenue and refunds
  const financialTotalsData = useMemo(() => {
    const months = Array.from({ length: 12 }, (_, i) => getMonthName(i + 1));
    
    // Calculate total revenue and refunds by plan
    const planTotals = {
      free: { revenue: 0, refunds: 0 },
      pro: { revenue: 0, refunds: 0 },
      premium: { revenue: 0, refunds: 0 }
    };

    // Sum up all financial data by plan
    stats.all_users.forEach(user => {
      const plan = user.subscription?.plan || 'free';
      const totalSpent = user.total_spent || 0;
      const totalRefunds = user.total_refunds || 0;
      
      planTotals[plan as keyof typeof planTotals].revenue += totalSpent;
      planTotals[plan as keyof typeof planTotals].refunds += totalRefunds;
    });

    // Get current month (October = index 9)
    const currentMonth = new Date().getMonth(); // 0-indexed (October = 9)
    
    // Create arrays with zeros for all months
    const totalCredits = Array(12).fill(0);
    const totalDebits = Array(12).fill(0);
    
    // Calculate total revenue and refunds across all plans
    const totalRevenue = planTotals.free.revenue + planTotals.pro.revenue + planTotals.premium.revenue;
    const totalRefundsAmount = planTotals.free.refunds + planTotals.pro.refunds + planTotals.premium.refunds;
    
    // Show progression from June to October (simulate growth)
    const startMonth = 5; // June (0-indexed)
    const endMonth = currentMonth; // Current month (October = 9)
    
    // Create a progression showing growth from June to current month
    for (let i = startMonth; i <= endMonth; i++) {
      const progressRatio = (i - startMonth + 1) / (endMonth - startMonth + 1);
      totalCredits[i] = totalRevenue * progressRatio;
      totalDebits[i] = totalRefundsAmount * progressRatio;
    }

    return {
      labels: months,
      datasets: [
        {
          label: "Total Revenue",
          data: totalCredits,
          borderColor: "rgba(34, 197, 94, 1)",    // green-500
          backgroundColor: "rgba(34, 197, 94, 0.1)", // green-500 with opacity
          tension: 0.2,
          fill: true,
        },
        {
          label: "Total Refunds",
          data: totalDebits,
          borderColor: "rgba(239, 68, 68, 1)",    // red-500
          backgroundColor: "rgba(239, 68, 68, 0.1)", // red-500 with opacity
          tension: 0.2,
          fill: true,
        },
      ],
    };
  }, [stats.all_users]);

  const prepareUserTypeData = () => {
    return {
      labels: ["Free", "Pro", "Premium"],
      datasets: [
        {
          label: "Users by Plan",
          data: [
            stats.users_by_type.free,
            stats.users_by_type.pro,
            stats.users_by_type.premium,
          ],
          backgroundColor: [
            "rgba(156, 163, 175, 0.7)", // gray-400
            "rgba(249, 115, 22, 0.7)",  // orange-500
            "rgba(236, 72, 153, 0.7)",  // pink-500
          ],
          borderColor: [
            "rgba(156, 163, 175, 1)",   // gray-400
            "rgba(249, 115, 22, 1)",    // orange-500
            "rgba(236, 72, 153, 1)",    // pink-500
          ],
          borderWidth: 1,
        },
      ],
    };
  };

  const prepareMonthlyContentData = () => {
    const months = Array.from({ length: 12 }, (_, i) => getMonthName(i + 1));
    const recipeData = Array.from({ length: 12 }, (_, i) => {
      const monthKey = (i + 1).toString();
      return stats.monthly_data[monthKey]?.recipes || 0;
    });

    const mealPlanData = Array.from({ length: 12 }, (_, i) => {
      const monthKey = (i + 1).toString();
      return stats.monthly_data[monthKey]?.meal_plans || 0;
    });

    return {
      labels: months,
      datasets: [
        {
          label: "Recipes",
          data: recipeData,
          borderColor: "rgba(249, 115, 22, 1)",    // orange-500
          backgroundColor: "rgba(249, 115, 22, 0.1)", // orange-500 with opacity
          tension: 0.2,
          fill: true,
        },
        {
          label: "Meal Plans",
          data: mealPlanData,
          borderColor: "rgba(236, 72, 153, 1)",    // pink-500
          backgroundColor: "rgba(236, 72, 153, 0.1)", // pink-500 with opacity
          tension: 0.2,
          fill: true,
        },
      ],
    };
  };

  const userTypeChartData = prepareUserTypeData();
  const monthlyContentChartData = prepareMonthlyContentData();

  return (
    <div className="space-y-8">
      {/* Header */}
      <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
        <div className="flex items-center space-x-3">
          <div className="p-2 bg-primary-light rounded-lg">
            <BarChart3 className="h-6 w-6 text-primary" />
          </div>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Dashboard Overview</h1>
            <p className="text-gray-600">Key metrics and analytics for your platform</p>
          </div>
        </div>
      </div>

      {/* Summary cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-6 gap-6">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden border-l-4 border-l-primary hover:shadow-md transition-shadow">
          <div className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-primary-light text-primary mr-4">
                <UserPlus className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">New Users</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.new_users_this_month}
                </p>
              </div>
            </div>
            <div className="mt-3">
              <p className="text-xs text-gray-500">Current month</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden border-l-4 border-l-orange-500 hover:shadow-md transition-shadow">
          <div className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-orange-100 text-orange-600 mr-4">
                <Users className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Total Users</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.users_by_type.free +
                    stats.users_by_type.pro +
                    stats.users_by_type.premium}
                </p>
              </div>
            </div>
            <div className="mt-3">
              <p className="text-xs text-gray-500">All plans</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden border-l-4 border-l-orange-600 hover:shadow-md transition-shadow">
          <div className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-orange-100 text-orange-600 mr-4">
                <Utensils className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Recipes</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.total_recipes}
                </p>
              </div>
            </div>
            <div className="mt-3">
              <p className="text-xs text-gray-500">Total on platform</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden border-l-4 border-l-pink-500 hover:shadow-md transition-shadow">
          <div className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-pink-100 text-pink-600 mr-4">
                <ClipboardList className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Meal Plans</p>
                <p className="text-2xl font-bold text-gray-900">
                  {stats.total_meal_plans}
                </p>
              </div>
            </div>
            <div className="mt-3">
              <p className="text-xs text-gray-500">Total on platform</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden border-l-4 border-l-green-500 hover:shadow-md transition-shadow">
          <div className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-green-100 text-green-600 mr-4">
                <DollarSign className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Total Revenue</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(financialTotals.totalRevenue)}
                </p>
              </div>
            </div>
            <div className="mt-3">
              <p className="text-xs text-gray-500">All-time earnings</p>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden border-l-4 border-l-red-500 hover:shadow-md transition-shadow">
          <div className="p-6">
            <div className="flex items-center">
              <div className="p-3 rounded-full bg-red-100 text-red-600 mr-4">
                <DollarSign className="h-6 w-6" />
              </div>
              <div>
                <p className="text-sm font-medium text-gray-500">Total Refunds</p>
                <p className="text-2xl font-bold text-gray-900">
                  {formatCurrency(financialTotals.totalRefunds)}
                </p>
              </div>
            </div>
            <div className="mt-3">
              <p className="text-xs text-gray-500">All-time refunds</p>
            </div>
          </div>
        </div>
      </div>

      {/* Filter section */}
      <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
        <div className="flex items-center mb-4 text-gray-700">
          <Filter className="h-5 w-5 mr-2" />
          <h2 className="text-lg font-semibold">Filter statistics by date range</h2>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              Start Date
            </label>
            <input
              type="date"
              className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
              value={periodFilter.startDate || ""}
              onChange={onStartDateChange}
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 mb-1">
              End Date
            </label>
            <input
              type="date"
              className="mt-1 block w-full px-3 py-2 bg-white border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500 sm:text-sm"
              value={periodFilter.endDate || ""}
              onChange={onEndDateChange}
              min={periodFilter.startDate || undefined}
            />
          </div>
        </div>
        <div className="mt-4 flex gap-2">
          <button
            onClick={onClearFilter}
            className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-orange-500 focus:border-orange-500"
          >
            Clear Filter
          </button>
        </div>
      </div>

      {/* Period Statistics */}
      {loadingPeriod && (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center justify-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-orange-500"></div>
            <span className="ml-2 text-gray-600">Loading period statistics...</span>
          </div>
        </div>
      )}

      {periodStats && !loadingPeriod && (
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center mb-6 text-gray-700">
            <TrendingUp className="h-5 w-5 mr-2" />
            <h2 className="text-lg font-semibold">
              Statistics for{" "}
              {periodStats.period.startDate && periodStats.period.endDate ? 
                `${new Date(periodStats.period.startDate).toLocaleDateString()} - ${new Date(periodStats.period.endDate).toLocaleDateString()}` :
                periodStats.period.startDate ?
                `From ${new Date(periodStats.period.startDate).toLocaleDateString()}` :
                periodStats.period.endDate ?
                `Until ${new Date(periodStats.period.endDate).toLocaleDateString()}` :
                "Selected Period"
              }
            </h2>
          </div>

          {periodStats.users.total === 0 && periodStats.content.recipes === 0 && periodStats.content.meal_plans === 0 ? (
            <div className="text-center py-8">
              <p className="text-gray-500 text-lg">No data found for the selected period</p>
              <p className="text-gray-400 text-sm mt-2">Try adjusting your date range to see results</p>
            </div>
          ) : (
            <>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
            <div className="text-center p-4 bg-orange-50 rounded-lg">
              <Users className="h-8 w-8 text-orange-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-orange-600">
                {periodStats.users.total}
              </p>
              <p className="text-sm text-gray-600">Total Users</p>
            </div>
            <div className="text-center p-4 bg-orange-100 rounded-lg">
              <Utensils className="h-8 w-8 text-orange-700 mx-auto mb-2" />
              <p className="text-2xl font-bold text-orange-700">
                {periodStats.content.recipes}
              </p>
              <p className="text-sm text-gray-600">Recipes</p>
            </div>
            <div className="text-center p-4 bg-pink-50 rounded-lg">
              <ClipboardList className="h-8 w-8 text-pink-600 mx-auto mb-2" />
              <p className="text-2xl font-bold text-pink-600">
                {periodStats.content.meal_plans}
              </p>
              <p className="text-sm text-gray-600">Meal Plans</p>
            </div>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-lg font-semibold text-gray-800">
                {periodStats.users.free}
              </p>
              <p className="text-sm text-gray-600">Free Users</p>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-lg font-semibold text-gray-800">
                {periodStats.users.pro}
              </p>
              <p className="text-sm text-gray-600">Pro Users</p>
            </div>
            <div className="text-center p-3 bg-gray-50 rounded-lg">
              <p className="text-lg font-semibold text-gray-800">
                {periodStats.users.premium}
              </p>
              <p className="text-sm text-gray-600">Premium Users</p>
            </div>
          </div>
            </>
          )}
        </div>
      )}

      {/* Financial Charts - User Growth & Revenue */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* User Growth/Loss Chart */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-4 text-gray-700">
            <TrendingUp className="h-5 w-5 mr-2" />
            <h2 className="text-lg font-semibold">User Growth by Plan</h2>
          </div>
          <p className="text-sm text-gray-600 mb-4">Track user acquisition and growth trends by subscription plan</p>
          <div className="h-80">
            <Line 
              data={userGrowthData} 
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: "top" as const,
                  },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                  },
                },
              }}
            />
          </div>
        </div>

        {/* Financial Totals Chart */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
          <div className="flex items-center mb-4 text-gray-700">
            <DollarSign className="h-5 w-5 mr-2" />
            <h2 className="text-lg font-semibold">Financial Movements</h2>
          </div>
          <p className="text-sm text-gray-600 mb-4">Total revenue inflow and refund outflow over time</p>
          <div className="h-80">
            <Line 
              data={financialTotalsData} 
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: "top" as const,
                  },
                  tooltip: {
                    callbacks: {
                      label: (context) => {
                        const value = context.parsed.y;
                        return `${context.dataset.label}: ${formatCurrency(value)}`;
                      },
                    },
                  },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                    ticks: {
                      callback: function(value) {
                        return formatCurrency(Number(value));
                      },
                    },
                  },
                },
              }}
            />
          </div>
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        {/* User Distribution Pie Chart */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center mb-4 text-gray-700">
            <PieChartIcon className="h-5 w-5 mr-2" />
            <h2 className="text-lg font-semibold">User Distribution by Plan</h2>
          </div>
          <div className="h-80">
            <Pie
              data={userTypeChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: "bottom" as const,
                  },
                  tooltip: {
                    callbacks: {
                      label: (context) => {
                        const total = context.dataset.data.reduce(
                          (a: number, b: number) => a + b,
                          0
                        );
                        const percentage = ((context.parsed / total) * 100).toFixed(1);
                        return `${context.label}: ${context.parsed} (${percentage}%)`;
                      },
                    },
                  },
                },
              }}
            />
          </div>
        </div>

        {/* Monthly Content Chart */}
        <div className="bg-white p-6 rounded-lg shadow-sm border border-gray-200">
          <div className="flex items-center mb-4 text-gray-700">
            <BarChart3 className="h-5 w-5 mr-2" />
            <h2 className="text-lg font-semibold">Monthly Content Creation</h2>
          </div>
          <div className="h-80">
            <Line
              data={monthlyContentChartData}
              options={{
                responsive: true,
                maintainAspectRatio: false,
                plugins: {
                  legend: {
                    position: "top" as const,
                  },
                },
                scales: {
                  y: {
                    beginAtZero: true,
                  },
                },
              }}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default DashboardContent;
